import {
    Component, OnInit, OnDestroy, ChangeDetectionStrategy, Input, Output, EventEmitter, ChangeDetectorRef, TemplateRef, ViewChild, OnChanges,
    Injector, Renderer2, ComponentFactoryResolver, EmbeddedViewRef, ApplicationRef, ComponentRef, SimpleChanges,
} from '@angular/core';
import { DropdownVendorComponent } from './dropdown-vendor.shared.component';
import { ScrollService } from '@shared/core/directives/scroll/scroll.shared.service';
import { trigger, transition, state, style, animate } from '@angular/animations';

@Component({
    selector: 'shared-drop-down',
    templateUrl: './drop-down.shared.component.html',
    styleUrls: ['./drop-down.shared.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    animations: [
        trigger('openClosed', [
            state('open', style({
                opacity: 1,
            })),
            state('closed', style({
                opacity: 0
            })),
            transition('closed => open', [
                animate('0.3s 0.2s ease-in')
            ]),
            transition('open => closed', [
                animate('0.3s')
            ]),
        ]),
    ]
})
export class DropDownComponent implements OnInit, OnChanges, OnDestroy {
    /* When there is problem with z-index, when global - options list will be projected outside of current html scope (in body) */
    private _vendorComponentRef: ComponentRef<DropdownVendorComponent>;
    private _isOpen: boolean = false;

    @Input() public open!: boolean;
    @Input() public projection: 'local' | 'global' = 'local';
    @Input() public projectionStyle: { [key: string]: number | string; } = null;
    @Input() public closeGlobalOnScroll: boolean = true;

    @Input() public mode: 'menu' | null = null;
    @Input() public theme: OLO.Components.DropDown.IDropDownTheme = 'common';
    @Input() public size!: OLO.Components.DropDown.IDropDownSize;
    @Input() public position: OLO.Components.DropDown.IDropDownPosition = 'bl';
    @Input() public selected: OLO.Components.DropDown.IDropDownOption = null;
    @Input() public width: OLO.Components.DROPDOWN_WIDTH = null;
    @Input() public preContent!: TemplateRef<any>;
    @Input() public postContent!: TemplateRef<any>;
    @Input() public openEvent: string = 'click';
    @Input() public refElem!: HTMLElement;

    @Input() public options: OLO.Components.DropDown.IDropDownOption[] = [];
    @Input() public optionTemplate: TemplateRef<any> = null;
    //
    //  <ng-template #exampleName let-item="item">
    //      {{item?.Name}}
    //      <shared-badge class="auth-link__badge" size="xs" *ngIf="item?.Counter">{{item?.Counter}}
    //      </shared-badge>
    //  </ng-template>
    //
    @Output() public onSelected: EventEmitter<OLO.Components.DropDown.IDropDownOption> = new EventEmitter();
    @Output() public onToggle: EventEmitter<boolean> = new EventEmitter();

    @ViewChild('dropdownOptionsList', { static: true }) public dropdownOptionsList: TemplateRef<any>;
    @ViewChild('dropdownTriggerContent', { static: true }) public dropdownTriggerContent: TemplateRef<any>;

    constructor(
        private _scrollService: ScrollService,
        private _changeDetectorRef: ChangeDetectorRef,

        private _renderer2: Renderer2,
        private _componentFactoryResolver: ComponentFactoryResolver,
        private _injector: Injector,
        private _appRef: ApplicationRef,
    ) { }

    public get isOpen(): boolean {
        return this._isOpen;
    }

    ngOnInit(): void {
        if (this.projection === 'global') {
            this._createVendorComponentView();
        }

        if (typeof this.open === 'boolean') {
            this._isOpen = this.open;
        }
    }

    ngOnChanges(changes: SimpleChanges) {
        if (typeof changes?.open?.currentValue === 'boolean') {
            this.handleToggle(changes.open.currentValue);
        }

        this._updateScroll();

        setTimeout(() => {
            this._changeDetectorRef.detectChanges();
        }, 1000);
    }

    ngOnDestroy(): void {
        this._destroyVendorComponentView();
    }

    private _updateScroll(): void {
        this._scrollService.requestUpdate();
        this._changeDetectorRef.detectChanges();
    }

    private _setStylesToVendorComponentView(): void {
        if (this.projection !== 'global' || !this._vendorComponentRef || !this.dropdownTriggerContent.elementRef.nativeElement) return;

        const refToElem: HTMLElement = this.refElem || this.dropdownTriggerContent.elementRef.nativeElement.parentElement.firstElementChild;
        if (!refToElem) return;

        const coords: DOMRect | ClientRect = refToElem.getClientRects()[0];

        const styles = {
            'width': coords.width + 'px',
            'height': coords.height + 'px',
            'top': (coords.top + (window.scrollY || window.pageYOffset)) + 'px',
            'left': coords.left + 'px',
            'position': 'absolute',
            'z-index': 20,
        };

        this._vendorComponentRef.instance.styles = {
            ...styles,
            ...this.projectionStyle
        };
    }

    private _unsetStylesToVenderoComponentView(): void {
        if (this.projection !== 'global' || !this._vendorComponentRef) return;

        this._vendorComponentRef.instance.styles = {
            display: 'none',
        };
    }

    private _createVendorComponentView(): void {
        //
        //  When projection is set to 'global', we need to display dropdown directly in BODY element but
        //  keeping context. We use dropdown-vendor component to host specified template 'dropdownOptionsList'.
        //  First we create this component dynamiacly and then we provide our template with dropdown options.
        //

        /* Create a component reference from the component - to host container that will project our template ref */
        this._vendorComponentRef = this._componentFactoryResolver
            .resolveComponentFactory(DropdownVendorComponent)
            .create(this._injector);

        /* Attach component to the appRef so that it's inside the ng component tree */
        this._appRef.attachView(this._vendorComponentRef.hostView);

        /* Get DOM element from component */
        const domElem: HTMLElement = (this._vendorComponentRef.hostView as EmbeddedViewRef<DropdownVendorComponent>).rootNodes[0] as HTMLElement;

        /* Append DOM element to the body */
        this._renderer2.appendChild(document.body, domElem);

        /* Inject input */
        this._vendorComponentRef.instance.tplRef = this.dropdownOptionsList;
        this._vendorComponentRef.instance.width = 'full';
    }

    private _destroyVendorComponentView(): void {
        if (!this._vendorComponentRef) return;

        this._appRef.detachView(this._vendorComponentRef.hostView);
        this._vendorComponentRef.destroy();
    }

    public optionSelected(option: OLO.Components.DropDown.IDropDownOption): void {
        if (this.mode !== 'menu') {
            this.selected = option;
        }
        this.onSelected.emit(option);

        this._changeDetectorRef.detectChanges();
    }

    public trackBy(index: number, option: OLO.Components.DropDown.IDropDownOption): number | string {
        return option?.Id || index;
    }

    public handleToggle(isOpen: boolean): void {
        const isDiff = this._isOpen !== isOpen;
        if (!isDiff) return;

        this._isOpen = isOpen;

        if (isOpen) {
            this._setStylesToVendorComponentView();
        } else {
            this._unsetStylesToVenderoComponentView();
        }

        this.onToggle.emit(isOpen);
        this._changeDetectorRef.detectChanges();

        /* Loose button focus */
        if (!isOpen) {
            if (this.projection !== 'global' || !this._vendorComponentRef || !this.dropdownTriggerContent.elementRef.nativeElement) return;
            const btn = this.dropdownTriggerContent.elementRef.nativeElement.parentElement.firstElementChild.querySelector('button');
            if (btn) {
                btn.blur();
            }
        }

        setTimeout(() => {
            this._scrollService.requestUpdate();
        }, 100);
    }
}
